import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/useAuth'
import {
  formatNotifTime,
  notificationActions,
  notificationCopy,
  severity,
  type NotificationActionKey,
  type Severity,
} from '../lib/notifications'
import type { Product, ThresholdNotification } from '../lib/types'
import { LinkButton } from './ui/Button'
import { WarningIcon } from './ui/WarningIcon'

// One notification row: the prototype's `.notif-item` (subscription-portal.html
// `entryHTML` and its CSS, value for value — MS-868, ARCHITECTURE_DECISIONS.md
// §49). Icon square, title, product line, body, right-aligned actions, and a
// top-right cluster of timestamp, read toggle and dismiss. An unread row is
// tinted and one font weight heavier on each line.
//
// One deliberate layout difference: the top-right cluster sits in the
// title's row (in flow) rather than absolutely over the row, so a title that
// does not fit beside it wraps instead of overlapping the timestamp — see the
// comment on the title row.

// The trial banner's own pair (components/TrialBanner.tsx): a yellow wash over
// the card surface and a fixed #e0a800 glyph, the same in both themes. The
// "Trial ends in 7 days" notice is the one non-threshold row (PO Q5).
const TRIAL_ICON_BG = 'bg-[var(--bg-card)] bg-[image:linear-gradient(rgba(255,193,7,.13),rgba(255,193,7,.13))]'
const TRIAL_ICON_STROKE = '#e0a800'

const ICON_BG: Record<Severity, string> = {
  warning: 'bg-[var(--warning-bg)]',
  error: 'bg-[var(--danger-bg)]',
  trial: TRIAL_ICON_BG,
}
const ICON_STROKE: Record<Severity, string> = {
  warning: 'var(--warning)',
  error: 'var(--danger)',
  trial: TRIAL_ICON_STROKE,
}

function HourglassIcon({ stroke }: Readonly<{ stroke: string }>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={16}
      height={16}
      fill="none"
      stroke={stroke}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      data-testid="notif-hourglass"
    >
      <path d="M5 22h14" />
      <path d="M5 2h14" />
      <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
      <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
    </svg>
  )
}

/** The prototype's read-toggle envelopes: open when read, filled (with the
 * flap cut out in the card colour) when unread. The open one is also the
 * mark-all icon. */
export function OpenEnvelopeIcon({ size }: Readonly<{ size: number }>) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21.2 8.4c.5.38.8.97.8 1.6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V10a2 2 0 0 1 .8-1.6l8-6a2 2 0 0 1 2.4 0z" />
      <path d="m22 10-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 10" />
    </svg>
  )
}

function ClosedEnvelopeIcon() {
  return (
    <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2" fill="currentColor" />
      <path d="m22 7-10 6L2 7" stroke="var(--bg-card)" />
    </svg>
  )
}

function actionLabelKey(key: NotificationActionKey, product: Product) {
  switch (key) {
    case 'buyMore':
      return product === 'Mambu Agents' ? 'notifications.buyMoreCredits' : 'notifications.buyMoreInvocations'
    case 'allowExcess':
      return 'notifications.allowExcess'
    case 'viewUsage':
      return 'notifications.viewUsage'
    case 'viewSubscription':
      return 'notifications.viewSubscription'
    case 'viewLimits':
      return 'notifications.viewLimits'
  }
}

export function NotificationItem({
  notification: n,
  onSetRead,
  onDismiss,
  onNavigate,
}: Readonly<{
  notification: ThresholdNotification
  onSetRead: (id: string, read: boolean) => void
  onDismiss: (id: string) => void
  /** Called when an action navigates away (the dropdown closes on it). */
  onNavigate?: () => void
}>) {
  const { t, i18n } = useTranslation()
  const { accountName } = useAuth()
  const level = severity(n)
  const { title, body } = notificationCopy(t, n, { accountName: accountName ?? null, language: i18n.language })
  const toggleLabel = n.read ? t('notifications.markUnread') : t('notifications.markRead')
  const unread = !n.read

  return (
    <div
      data-testid="notif-item"
      data-unread={unread ? 'true' : 'false'}
      className={`flex gap-[11px] px-3.5 py-[13px] border-b border-[var(--border-light)] last:border-b-0 ${
        unread ? 'bg-[var(--accent-subtle)]' : ''
      }`}
    >
      <div
        data-testid="notif-icon"
        data-severity={level}
        className={`shrink-0 w-[30px] h-[30px] rounded-[7px] flex items-center justify-center ${ICON_BG[level]}`}
      >
        {level === 'trial' ? (
          <HourglassIcon stroke={ICON_STROKE[level]} />
        ) : (
          <WarningIcon size={16} stroke={ICON_STROKE[level]} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        {/* The title shares its row with the top-right cluster (timestamp,
            read toggle, dismiss), so a long title — a user's email, or a
            language whose date is wider than the prototype's "Oct 15" — wraps
            beside the cluster instead of running under it. The prototype's
            `.notif-top` is absolute at 11px/11px and the title reserves a
            92px right padding; the cluster's 95px minimum width and its
            -2px/-3px offsets put it, and the title's wrap point, exactly
            there whenever the cluster fits in that space, and it only grows
            (narrowing the title) when it does not. */}
        <div className="flex items-start">
          <div
            data-testid="notif-title"
            className={`flex-1 min-w-0 break-words text-[13px] text-[var(--text-primary)] ${unread ? 'font-bold' : 'font-semibold'}`}
          >
            {title}
          </div>
          <div
            data-testid="notif-top"
            className="shrink-0 min-w-[95px] -mt-[2px] -mr-[3px] pl-2 flex items-center justify-end gap-[7px]"
          >
            <span className="text-[11px] text-[var(--text-tertiary)] whitespace-nowrap">
              {formatNotifTime(n.notifiedAt, i18n.language)}
            </span>
            <button
              type="button"
              onClick={() => onSetRead(n.id, !n.read)}
              aria-label={toggleLabel}
              title={toggleLabel}
              // An unread row's envelope keeps the primary colour on hover too
              // (the prototype's `.notif-item.unread .notif-read-toggle` outranks
              // `.notif-read-toggle:hover`).
              className={`shrink-0 w-[18px] h-[18px] p-0 rounded-[4px] flex items-center justify-center hover:bg-[var(--accent-subtle)] ${
                unread ? 'text-[var(--accent)]' : 'text-[var(--text-tertiary)] hover:text-[var(--accent-text)]'
              }`}
            >
              {n.read ? <OpenEnvelopeIcon size={12} /> : <ClosedEnvelopeIcon />}
            </button>
            <button
              type="button"
              onClick={() => onDismiss(n.id)}
              aria-label={t('notifications.dismiss')}
              className="shrink-0 w-[18px] h-[18px] p-0 rounded-[4px] flex items-center justify-center text-[var(--danger)] hover:bg-[var(--danger-bg)]"
            >
              <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
        {/* The product is a proper noun, never translated. */}
        <div className={`text-[12px] text-[var(--text-secondary)] mt-px ${unread ? 'font-semibold' : 'font-medium'}`}>
          {n.product}
        </div>
        {body && (
          <div
            data-testid="notif-body"
            className={`break-words text-[12px] text-[var(--text-secondary)] leading-[1.45] mt-[3px] ${unread ? 'font-medium' : 'font-normal'}`}
          >
            {body}
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-2 mt-[9px]">
          {/* `leading-[normal]`: the prototype's actions are <button>s, which
              do not inherit the body's line height; ours are links. */}
          {notificationActions(n).map((action) => (
            <LinkButton
              key={action.key}
              to={action.to}
              variant={action.variant}
              onClick={onNavigate}
              className={`!text-[12px] !leading-[normal] !px-3 !py-[5px] ${action.variant === 'ghost' ? '!bg-[var(--bg-card)]' : ''}`}
            >
              {t(actionLabelKey(action.key, n.product))}
            </LinkButton>
          ))}
        </div>
      </div>
    </div>
  )
}
